import { useState } from 'react';

import { Trans, useLingui } from '@lingui/react/macro';
import { Button } from '@repo/videa-components/src/index';
import classNames from 'classnames';

import { AIImpactJsonInputModal } from '@/common-components/debug/AIImpactJsonInputModal';
import { FeedbackWidget } from '@/common-components/elements/FeedbackWidget/FeedbackWidget';
import type { FeedbackOption } from '@/common-components/elements/FeedbackWidget/types';
import { useDevtoolsShortcut } from '@/utils/hooks/useDevtoolsShortcut';
import { mixpanelEvents, useTrackEvent } from '@/utils/mixpanel';

import { ExternalError } from '../ExternalError';
import { useGetPracticeAIImpact, usePracticeAIImpactSummary } from '../hooks';
import type { HighlightCategory, PracticeAIImpactData } from '../types';
import { ModalType } from '../types';

import { AIImpactBarChart } from './AIImpactBarChart';
import { AIImpactCard } from './AIImpactCard';

export const AIImpact = () => {
  const { t } = useLingui();
  const { data, isLoading, isError } = useGetPracticeAIImpact();
  const { trackEvent } = useTrackEvent();
  const { showDevtools } = useDevtoolsShortcut();
  const [isJsonModalOpen, setIsJsonModalOpen] = useState(false);

  const getTreatmentLabel = (category: HighlightCategory) => {
    switch (category) {
      case 'Crowns':
        return t`crown`;
      case 'Extraction':
        return t`extraction`;
      case 'Fillings':
        return t`filling`;
      case 'Implant':
        return t`implant`;
      case 'Incipient':
        return t`incipient`;
      case 'Root Canal':
        return t`root canal`;
      case 'SRP':
        return t`srp`;
      default:
        return category;
    }
  };

  const {
    showPatientEducationCard,
    showCaseAcceptanceCard,
    showTreatmentPlanningCard,
    patientEducationDetails,
    caseAcceptanceDetails,
    patientEducationRate,
    caseAcceptanceRate,
    treatmentPlanValueRate,
    selectedTreatmentPlanCategory,
    selectedTreatmentPlanPreAiValue,
    selectedTreatmentPlanPostAiValue,
  } = usePracticeAIImpactSummary(data as PracticeAIImpactData | undefined);

  const numberOfCards = [
    showPatientEducationCard,
    showCaseAcceptanceCard,
    showTreatmentPlanningCard,
  ].filter(Boolean).length;

  const PositiveFeedbackOptions: FeedbackOption[] = [
    {
      label: t`Makes AI value clear`,
      value: 'CLEAR_AI_VALUE',
    },
    {
      label: t`Easy to understand`,
      value: 'EASY_TO_UNDERSTAND',
    },
    {
      label: t`Helps me track impact`,
      value: 'HELPS_TRACK_IMPACT',
    },
    {
      label: t`Good data explanations`,
      value: 'GOOD_DATA_EXPLANATIONS',
    },
    {
      label: t`Data is accurate`,
      value: 'ACCURATE_DATA',
    },
  ];

  const NegativeFeedbackOptions: FeedbackOption[] = [
    {
      label: t`Data does not seem accurate`,
      value: 'INACCURATE_DATA',
    },
    {
      label: t`Not enough detail`,
      value: 'NOT_ENOUGH_DETAIL',
    },
    {
      label: t`Too hard to understand`,
      value: 'TOO_HARD_TO_UNDERSTAND',
    },
    {
      label: t`Data explanations are unclear`,
      value: 'UNCLEAR_DATA_EXPLANATIONS',
    },
  ];

  const handleRate = (rating: 'up' | 'down') => {
    trackEvent(mixpanelEvents.externalAiImpactRating, {
      rating,
    });
  };

  const handleFeedback = (
    rating: 'up' | 'down',
    selectedOptions: string[],
    additionalComments?: string,
  ) => {
    trackEvent(mixpanelEvents.externalAiImpactFeedback, {
      rating,
      comment: additionalComments,
      selectedOptions,
    });
  };

  if (isError) {
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-h-full">
        <ExternalError
          message={t`Please reopen the page. If the problem continues, contact support for help.`}
          mode="light"
          showButtons={false}
          source={ModalType.AI_IMPACT}
          title={t`Trouble loading content.`}
        />
      </div>
    );
  }

  const showTreatmentLabel =
    selectedTreatmentPlanCategory &&
    selectedTreatmentPlanCategory !== 'Overall';
  const treatmentPlanLabel = showTreatmentLabel
    ? getTreatmentLabel(selectedTreatmentPlanCategory)
    : null;

  if (isLoading || !data) {
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-h-full">
        <div className="vai-animate-spin vai-rounded-full vai-h-8 vai-w-8 vai-border-[3px] vai-border-t-transparent vai-border-sapphire-600 vai-mx-auto" />
      </div>
    );
  }

  return (
    <div className="vai-flex vai-flex-col vai-items-center">
      {/* Mock AI Impact JSON Button - only show when RBL devtools are enabled */}
      {showDevtools && (
        <div className="vai-fixed vai-top-5 vai-right-2.5 vai-z-[9999]">
          <Button
            className="vai-bg-blue-600 hover:vai-bg-blue-700 vai-text-white vai-px-3 vai-py-2 vai-text-xs vai-font-medium"
            onClick={() => setIsJsonModalOpen(true)}
          >
            <Trans>Mock AI Impact JSON</Trans>
          </Button>
        </div>
      )}

      <p className="vai-font-merriweather vai-font-bold vai-text-2xl vai-text-slate-900">
        <Trans>Impact of Detect AI on Your Practice</Trans>
      </p>
      <p className="vai-mt-3 vai-font-inter vai-text-navy-500">
        <Trans>
          Optimizing the right care for the right patients via AI education and
          acceptance
        </Trans>
      </p>

      <div
        className={classNames('vai-mt-8 vai-grid vai-gap-3', {
          'vai-grid-cols-1': numberOfCards === 1,
          'vai-grid-cols-2': numberOfCards === 2,
          'vai-grid-cols-3': numberOfCards === 3,
        })}
      >
        {showPatientEducationCard && (
          <AIImpactCard
            colorVariant="chart-normal-1"
            details={
              patientEducationDetails.length
                ? patientEducationDetails
                : undefined
            }
            featuredRate={
              patientEducationRate
                ? {
                    value: patientEducationRate,
                    prefix: t`AI is helping educate patients in`,
                    suffix: t`of your treatment cases`,
                  }
                : undefined
            }
            title={t`Patient Education`}
            type="patient-education"
          />
        )}
        {showCaseAcceptanceCard && (
          <AIImpactCard
            colorVariant="chart-normal-2"
            details={
              caseAcceptanceDetails.length ? caseAcceptanceDetails : undefined
            }
            featuredRate={
              caseAcceptanceRate
                ? {
                    value: caseAcceptanceRate,
                    prefix: t`Patients accepted`,
                    suffix: t`of your AI-Supported cases`,
                  }
                : undefined
            }
            title={t`Case Acceptance`}
            type="case-acceptance"
          />
        )}
        {showTreatmentPlanningCard && (
          <AIImpactCard
            colorVariant="chart-normal-3"
            featuredRate={
              treatmentPlanValueRate
                ? {
                    value: treatmentPlanValueRate,
                    prefix: showTreatmentLabel
                      ? t`Your ${treatmentPlanLabel} treatment plan value increased`
                      : t`Your treatment plan value increased`,
                    suffix: t`since adopting AI`,
                  }
                : undefined
            }
            title={t`Treatment Planning`}
            type="treatment-planning"
          >
            <div className="vai-mt-4 vai-p-3 vai-rounded-md vai-bg-chart-normal-3 vai-bg-opacity-[.07]">
              <AIImpactBarChart
                highlightPostAiValue={selectedTreatmentPlanPostAiValue}
                highlightPreAiValue={selectedTreatmentPlanPreAiValue}
              />
            </div>
          </AIImpactCard>
        )}
      </div>

      <FeedbackWidget
        className="vai-mt-6"
        variant="minimalist"
        label={
          <p className="vai-font-inter vai-font-semibold vai-text-[16px] vai-text-gray-700">
            <Trans>Was this helpful?</Trans>
          </p>
        }
        onRate={handleFeedback}
        onRatingButtonClick={handleRate}
        downOptions={NegativeFeedbackOptions}
        upOptions={PositiveFeedbackOptions}
      />

      <AIImpactJsonInputModal
        isOpen={isJsonModalOpen}
        onClose={() => setIsJsonModalOpen(false)}
      />
    </div>
  );
};
